import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, Settings, TrendingUp, Users, UserCog, Calendar, BarChart3, Wrench, Calculator, FileText } from 'lucide-react';
import ProfilePicture from './ProfilePicture';
import NotificationIcon from './NotificationIcon';
import HotelOnlineLogo from './HotelOnlineLogo';
import { User } from '../models/User';
import { getUnreadMessageCount } from '../data/messageDatabase';

interface AppHeaderProps {
  user: User | null;
  currentPage: string;
  onLogout: () => void;
  onNotificationClick?: () => void;
}

function AppHeader({ user, currentPage, onLogout, onNotificationClick }: AppHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Update unread count when component mounts and periodically
  useEffect(() => {
    if (!user?.id) return;

    const updateUnreadCount = async () => {
      try {
        const count = await getUnreadMessageCount(user.id);
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to get unread message count:', error);
        setUnreadCount(0);
      }
    };

    // Initial update
    updateUnreadCount();

    // Set up interval to check for new messages
    const interval = setInterval(updateUnreadCount, 1000);

    return () => clearInterval(interval);
  }, [user?.id]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  if (!user) {
    return null;
  }

  const displayName = user.fullName || user.email || 'Unknown User';

  const navigationItems = [
    {
      path: '/',
      label: 'Timeline',
      icon: TrendingUp,
      isActive: location.pathname === '/'
    },
    {
      path: '/meetings',
      label: 'Meetings',
      icon: Calendar,
      isActive: location.pathname === '/meetings'
    },
    {
      path: '/reports',
      label: 'Reports',
      icon: FileText,
      isActive: location.pathname.startsWith('/reports')
    },
    {
      path: '/marketplace',
      label: 'Marketplace',
      icon: Users,
      isActive: location.pathname === '/marketplace'
    }
  ];

  if (user.isSuperAdmin) {
    navigationItems.push({
      path: '/admin',
      label: 'Admin',
      icon: Wrench,
      isActive: location.pathname === '/admin'
    });
  }

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex flex-col items-start">
            <img 
              src="/hotelonline-logo.jpg" 
              alt="HotelOnline" 
              className="h-8 w-auto mb-1"
            />
            <div className="text-2xl font-semibold" style={{ color: '#2FB3D3', fontSize: '20px', letterSpacing: '0.5px' }}>
              Shareholder Portal
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Desktop User info and controls */}
            <div className="hidden md:block mr-4 text-sm text-gray-600">
              <span className="font-medium">{displayName}</span>
              <span className="ml-2 text-xs bg-gray-100 py-1 px-2 rounded-full">
                {user.isAdmin ? "Admin" : "User"}
              </span>
            </div>
            
            {/* Desktop Controls */}
            <div className="hidden md:flex items-center space-x-4">
              <button 
                onClick={onLogout}
                className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                title="Log Out"
              >
                <LogOut className="w-5 h-5" />
              </button>
              <NotificationIcon 
                count={unreadCount} 
                onClick={onNotificationClick || (() => handleNavigation('/inbox'))} 
              />
              <button 
                onClick={() => handleNavigation('/settings')}
                className={`p-2 rounded-full ${location.pathname === '/settings' ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} transition-colors`}
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
              <ProfilePicture 
                userId={user.id}
                fullName={displayName}
                profilePictureUrl={user.profilePictureUrl}
                profilePicture={user.profilePicture}
                size="sm"
              />
            </div>

            {/* Mobile Hamburger Menu */}
            <div className="md:hidden relative" ref={menuRef}>
              <button
                onClick={toggleMenu}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                aria-expanded={isMenuOpen}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>

              {/* Mobile Dropdown Menu */}
              {isMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-1">
                    {/* User Info in Mobile Menu */}
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center space-x-3">
                        <ProfilePicture 
                          userId={user.id}
                          fullName={displayName}
                          profilePictureUrl={user.profilePictureUrl}
                          profilePicture={user.profilePicture}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                          <p className="text-xs text-gray-500">
                            {user.isAdmin ? "Admin" : "User"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Navigation Items */}
                    {navigationItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full text-left px-4 py-3 text-sm flex items-center space-x-3 ${
                          item.isActive
                            ? 'bg-blue-50 text-blue-700 border-r-4 border-blue-500'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </button>
                    ))}

                    {/* Mobile Controls */}
                    <div className="border-t border-gray-200 py-1">
                      <button
                        onClick={() => handleNavigation('/inbox')}
                        className="w-full text-left px-4 py-3 text-sm flex items-center space-x-3 text-gray-700 hover:bg-gray-50"
                      >
                        <NotificationIcon count={unreadCount} onClick={() => {}} />
                        <span>Inbox</span>
                        {unreadCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-1">
                            {unreadCount}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => handleNavigation('/settings')}
                        className={`w-full text-left px-4 py-3 text-sm flex items-center space-x-3 ${
                          location.pathname === '/settings'
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Settings className="w-5 h-5" />
                        <span>Settings</span>
                      </button>
                      <button
                        onClick={onLogout}
                        className="w-full text-left px-4 py-3 text-sm flex items-center space-x-3 text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-5 h-5" />
                        <span>Log Out</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Desktop Navigation */}
      <nav className="bg-white border-b border-gray-200 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`border-b-2 ${
                    item.isActive
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  } inline-flex items-center px-1 pt-1 text-sm font-medium`}
                >
                  <item.icon className="w-5 h-5 mr-2" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

export default AppHeader;